import { Link } from 'react-router-dom'
import { Briefcase, Calendar, Users, XCircle, MessageSquare } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { useCustomerProjects, useDeveloperProjects, useCancelProject } from '@/hooks'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { Skeleton } from '@/components/ui/skeleton'
import { UserAvatar } from '@/components/UserAvatar'

function StatusBadge({ status }: { status: string }) {
  const label = status.replace(/_/g, ' ')

  const variant = (() => {
    switch (status) {
      case 'open':
        return 'secondary'
      case 'in_progress':
        return 'default'
      case 'completed':
        return 'outline'
      case 'cancelled':
        return 'destructive'
      default:
        return 'secondary'
    }
  })()

  return (
    <Badge variant={variant} className="uppercase tracking-wide">
      {label}
    </Badge>
  )
}

function ProjectTypeBadge({ type }: { type: 'private' | 'public' }) {
  return (
    <Badge variant={type === 'public' ? 'default' : 'secondary'} className="capitalize">
      {type}
    </Badge>
  )
}

function formatDate(input: string) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(input))
  } catch (error) {
    return input
  }
}

const cardGridStyles = 'grid gap-6 md:grid-cols-2 xl:grid-cols-3'

export default function ProjectsDashboard() {
  const { user } = useAuth()
  const role = user?.profile?.role
  const isCustomer = role === 'customer'
  const isDeveloper = role === 'developer'

  const cancelProject = useCancelProject()
  const customerProjects = useCustomerProjects()
  const developerProjects = useDeveloperProjects()

  if (!user) {
    return null
  }

  const renderCustomerView = () => {
    if (customerProjects.isLoading) {
      return (
        <div className={cardGridStyles}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-48 rounded-lg" />
          ))}
        </div>
      )
    }

    if (customerProjects.error) {
      return (
        <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-6 text-destructive">
          Unable to load your projects right now. Please try again shortly.
        </div>
      )
    }

    const projects = customerProjects.data ?? []

    if (projects.length === 0) {
      return (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center gap-4 py-16 text-center">
            <Briefcase className="h-10 w-10 text-muted-foreground" />
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">No projects yet</h3>
              <p className="max-w-md text-muted-foreground">
                Create your first project to start collaborating with developers. You can share
                briefs privately or list them publicly on the bounty board.
              </p>
            </div>
            <Button asChild>
              <Link to="/post">Post a Project</Link>
            </Button>
          </CardContent>
        </Card>
      )
    }

    return (
      <div className={cardGridStyles}>
        {projects.map((project) => {
          const isCancelling = cancelProject.isPending && cancelProject.variables === project.id
          const proposalsLabel = `${project.proposals.length} proposal${project.proposals.length === 1 ? '' : 's'}`
          const invitationsLabel = `${project.invitations.length} invite${project.invitations.length === 1 ? '' : 's'}`
          const acceptedProposal = project.proposals.find((p) => p.accepted)
          const isInProgress = project.status === 'in_progress'

          return (
            <Card key={project.id} className="flex h-full flex-col">
              <CardHeader className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <UserAvatar
                      size="sm"
                      avatarUrl={project.account_avatar_url}
                      name={project.account_name}
                      className="mt-1"
                    />
                    <div>
                      <CardTitle className="text-xl font-semibold leading-tight">
                        {project.title}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">{project.account_name}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <StatusBadge status={project.status} />
                    <ProjectTypeBadge type={project.project_type} />
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Posted {formatDate(project.created_at)}
                </div>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col justify-between gap-4">
                <p className="line-clamp-3 text-sm text-muted-foreground">{project.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <span>{proposalsLabel}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>{invitationsLabel}</span>
                  </div>
                  {project.skills.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {project.skills.map((skill) => (
                        <Badge key={skill.id} variant="outline" className="text-xs">
                          {skill.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Button asChild size="sm">
                    <Link to={`/projects/${project.id}`}>View details</Link>
                  </Button>
                  {isInProgress && acceptedProposal && (
                    <Button asChild size="sm" variant="outline" className="gap-2">
                      <Link
                        to={`/messages?user=${acceptedProposal.submitted_by_username}&project=${project.id}`}
                      >
                        <MessageSquare className="h-4 w-4" />
                        Project Chat
                      </Link>
                    </Button>
                  )}
                  {project.can_cancel && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => cancelProject.mutate(project.id)}
                      disabled={isCancelling}
                      className="gap-2"
                    >
                      <XCircle className="h-4 w-4" />
                      {isCancelling ? 'Cancelling…' : 'Cancel project'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    )
  }

  const renderDeveloperView = () => {
    if (developerProjects.isLoading) {
      return (
        <div className={cardGridStyles}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-44 rounded-lg" />
          ))}
        </div>
      )
    }

    if (developerProjects.error) {
      return (
        <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-6 text-destructive">
          Unable to load projects at the moment. Please refresh the page.
        </div>
      )
    }

    const data = developerProjects.data
    const invitedProjects = data?.invited_projects ?? []
    const publicProjects = data?.public_projects ?? []

    return (
      <div className="space-y-10">
        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Invited projects</h2>
            <p className="text-sm text-muted-foreground">
              These briefs were shared with you directly.
            </p>
          </div>

          {invitedProjects.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center gap-2 py-12 text-center text-muted-foreground">
                <Users className="h-8 w-8" />
                <p>
                  No direct invitations yet. Keep your profile up to date to attract more leads.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className={cardGridStyles}>
              {invitedProjects.map((project) => (
                <Card key={project.id} className="flex h-full flex-col">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <UserAvatar
                          size="sm"
                          avatarUrl={project.account_avatar_url}
                          name={project.account_name}
                          className="mt-1"
                        />
                        <div>
                          <CardTitle className="text-lg font-semibold">{project.title}</CardTitle>
                          <p className="text-sm text-muted-foreground">{project.account_name}</p>
                        </div>
                      </div>
                      <StatusBadge status={project.status} />
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col justify-between gap-4">
                    <p className="line-clamp-4 text-sm text-muted-foreground">
                      {project.description}
                    </p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{project.proposals.length} proposal(s)</span>
                      <span>Invited on {formatDate(project.created_at)}</span>
                    </div>
                    <Button asChild size="sm" className="self-start">
                      <Link to={`/projects/${project.id}`}>Open brief</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Bounty board highlights</h2>
              <p className="text-sm text-muted-foreground">
                Public briefs open to all developers. Browse the full list on the bounty board.
              </p>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/bounty-board">View bounty board</Link>
            </Button>
          </div>

          {publicProjects.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center gap-2 py-10 text-center text-muted-foreground">
                <Briefcase className="h-8 w-8" />
                <p>No public projects available right now. Check back soon.</p>
              </CardContent>
            </Card>
          ) : (
            <div className={cardGridStyles}>
              {publicProjects.slice(0, 6).map((project) => (
                <Card key={project.id} className="flex h-full flex-col">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <UserAvatar
                          size="sm"
                          avatarUrl={project.account_avatar_url}
                          name={project.account_name}
                          className="mt-1"
                        />
                        <div>
                          <CardTitle className="text-lg font-semibold">{project.title}</CardTitle>
                          <p className="text-sm text-muted-foreground">{project.account_name}</p>
                        </div>
                      </div>
                      <ProjectTypeBadge type={project.project_type} />
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col justify-between gap-4">
                    <p className="line-clamp-4 text-sm text-muted-foreground">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      <span>{project.proposals_count} proposal(s)</span>
                      <span>Updated {formatDate(project.updated_at)}</span>
                    </div>
                    <Button asChild size="sm" className="self-start">
                      <Link to={`/projects/${project.id}`}>View details</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-6xl space-y-10 px-4 py-10">
      <header className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Projects dashboard</h1>
            <p className="text-muted-foreground">
              {isCustomer
                ? 'Track briefs, proposals, and developer outreach in one place.'
                : 'Review invitations and discover public projects ready for proposals.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" size="sm">
              <Link to="/developers">Browse developers</Link>
            </Button>
            {isCustomer && (
              <Button asChild size="sm">
                <Link to="/post">Post project</Link>
              </Button>
            )}
            {isDeveloper && (
              <Button asChild size="sm">
                <Link to="/bounty-board">Bounty board</Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      {isCustomer ? renderCustomerView() : renderDeveloperView()}
    </div>
  )
}
